import type { GuideInteractionMetadata, GuideInteractionPayload } from './types';

export interface InteractionRequest {
  guideId: string;
  payload: GuideInteractionPayload;
}

/**
 * Wire shape esperada por `POST /v1/guides/:id/interactions` en el backend.
 * Nuestro tipo interno (`GuideInteractionPayload`) usa `action`/`stepIndex`;
 * el backend exige `interactionType`/`stepPosition`. Esta función centraliza
 * la traducción.
 */
interface WireInteraction {
  endUserId: string;
  sessionId?: string;
  interactionType: GuideInteractionPayload['action'];
  stepPosition?: number;
  pageUrl?: string;
  pagePath?: string;
  metadata?: GuideInteractionMetadata;
}

function toWire(payload: GuideInteractionPayload): WireInteraction {
  const wire: WireInteraction = {
    endUserId: payload.endUserId,
    interactionType: payload.action,
  };
  if (payload.sessionId) wire.sessionId = payload.sessionId;
  if (typeof payload.stepIndex === 'number') wire.stepPosition = payload.stepIndex;
  if (payload.pageUrl) wire.pageUrl = payload.pageUrl;
  if (payload.pagePath) wire.pagePath = payload.pagePath;
  if (payload.metadata) wire.metadata = payload.metadata;
  return wire;
}

/**
 * Cliente HTTP puro para registrar interacciones de guías. No hace retry
 * ni queue — esa responsabilidad vive en `GuideTrackerQueue`.
 *
 * `send` lanza en error (queue puede reintentar). `sendBeacon` es síncrono,
 * "best effort", para el cierre de página: usa `navigator.sendBeacon` con
 * el apiKey como query param `?key=` (la única forma de autenticar con
 * sendBeacon, ya que no permite headers custom — el backend lo soporta
 * desde el ApiKeyGuard).
 */
export class GuideTrackerClient {
  constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
  ) {}

  async send(request: InteractionRequest): Promise<void> {
    const url = this.buildUrl(request.guideId);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-Api-Key': this.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(toWire(request.payload)),
      credentials: 'omit',
    });

    if (!response.ok) {
      throw new Error(`guide interaction send failed: HTTP ${response.status}`);
    }
  }

  /**
   * Envío "fire-and-forget" en pagehide. Devuelve `false` si el navegador
   * no acepta el beacon (cola llena, no soportado) — en ese caso la queue
   * sabrá que perdió el envío.
   */
  sendBeacon(request: InteractionRequest): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return false;
    }
    const url = `${this.buildUrl(request.guideId)}?key=${encodeURIComponent(this.apiKey)}`;
    const blob = new Blob([JSON.stringify(toWire(request.payload))], {
      type: 'application/json',
    });
    try {
      return navigator.sendBeacon(url, blob);
    } catch {
      return false;
    }
  }

  private buildUrl(guideId: string): string {
    return `${this.apiUrl}/v1/guides/${encodeURIComponent(guideId)}/interactions`;
  }
}
